import { type AirActionTrackingLocation, useTrackAddedTagsToAssets, useTrackUpdatedAssetTags } from '@air/analytics';
import { Tags } from '@air/api';
import { type Clip, type Tag, type UnmadeTag } from '@air/api/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { xorBy } from 'lodash';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { updateCentralizedClipAction } from '~/store/centralizedClip/actions';
import { getClipWithoutWorkspaceIdKey } from '~/swr-hooks/useGetClipWithNoWorkspaceId';
import { getAssetGalleryItemType } from '~/utils/AssetUtils';
import { reportErrorToBugsnag } from '~/utils/ErrorUtils';
import {
  useRemoveAssetsWithoutTagsFromFilteredViews,
  useUpdateAssetInAllViews,
} from '~/utils/mutateUtils/GalleryAssets';

export const useUpdateClipTags = () => {
  const dispatch = useDispatch();
  const { updateAssetInAllViews } = useUpdateAssetInAllViews();
  const { removeAssetsWithoutTagsFromFilteredViews } = useRemoveAssetsWithoutTagsFromFilteredViews();
  const queryClient = useQueryClient();
  const { trackUpdatedAssetTags } = useTrackUpdatedAssetTags();
  const { currentWorkspace } = useCurrentWorkspace();

  const updateClipTags = useMutation({
    mutationFn: async ({ clip, tags }: { tags: Tag[]; clip: Pick<Clip, 'id' | 'tags' | 'type'> }) => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('No workspace id');
      }
      return Tags.diffTagsForClip({ workspaceId, tags, clip });
    },

    onSuccess: ({ clips }, { tags, clip }) => {
      try {
        /**
         * We want to invalidate all clips that have an id of clip.id
         */
        queryClient.invalidateQueries({
          predicate: (query) => {
            const clipKey = getClipWithoutWorkspaceIdKey({ clipId: clip.id });

            return query.queryKey[0] === clipKey[0] && query.queryKey[1] === clipKey[1].clipId;
          },
        });

        trackUpdatedAssetTags({
          asset: clip.id,
          tags,
        });

        // I'm not sure why clips can return undefined but this doesn't seem like a correct typing
        // Decided to protect against it otherwise. Left question in #backend channel: https://air-labs-team.slack.com/archives/CCS4C8ALT/p1592326824002400
        if (clips && clips[0]) {
          dispatch(updateCentralizedClipAction({ clip: { id: clip.id, tags } }));

          updateAssetInAllViews({
            asset: { id: clip.id, tags },
            type: getAssetGalleryItemType(clip),
          });

          removeAssetsWithoutTagsFromFilteredViews({
            clipIds: [clip.id],
            changedTags: xorBy(clip.tags, tags, 'label'),
            type: getAssetGalleryItemType(clip),
          });
        }
      } catch (error) {
        throw reportErrorToBugsnag({ error, context: 'Failed to update assets tags' });
      }
    },
  });

  return {
    updateClipTags,
  };
};

type CreateNewTagForAssetParams = {
  tag: UnmadeTag;
  clipId: Clip['id'];
  trackLocation: AirActionTrackingLocation;
};

export const useCreateNewTagForAsset = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { trackAddedTagsToAssets } = useTrackAddedTagsToAssets();
  const { currentWorkspace } = useCurrentWorkspace();

  const createNewTagForAsset = useCallback(
    async ({ tag, clipId, trackLocation }: CreateNewTagForAssetParams) => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('No workspace id');
      }
      try {
        const { clips } = await Tags.addOrCreateTagsOnClips({ workspaceId, tags: [tag], clips: [clipId] });

        /**
         * We want to invalidate all clips that have an id of clip.id
         */
        queryClient.invalidateQueries({
          predicate: (query) => {
            const clipKey = getClipWithoutWorkspaceIdKey({ clipId });

            return query.queryKey[0] === clipKey[0] && query.queryKey[1] === clipKey[1].clipId;
          },
        });

        if (clips && clips[0]) {
          trackAddedTagsToAssets({
            assets: [clipId],
            tags: clips[0].tags,
            location: trackLocation,
          });
          dispatch(updateCentralizedClipAction({ clip: { id: clipId, tags: clips[0].tags } }));
        }
      } catch (error) {
        throw reportErrorToBugsnag({ error, context: 'Failed to update assets tags' });
      }
    },
    [dispatch, queryClient, trackAddedTagsToAssets, currentWorkspace?.id],
  );

  return {
    createNewTagForAsset,
  };
};
